import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Reviews.css';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [externalVouches, setExternalVouches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const navigate = useNavigate();
  
  // Review form
  const [selectedOrder, setSelectedOrder] = useState('');
  const [reviewForm, setReviewForm] = useState({ stars: 5, text: '' });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    loadPublicReviews();
    loadExternalVouches();
    if (token) {
      loadMyReviews();
      loadEligibleOrders();
    } else {
      setLoading(false);
    }
  }, []);

  const loadExternalVouches = async () => {
    try {
      // Use our backend proxy to avoid CORS
      const res = await fetch('/api/external-vouches');
      if (res.ok) {
        const data = await res.json();
        setExternalVouches(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to load external vouches:', err);
    }
  };

  const loadPublicReviews = async () => {
    try {
      const res = await fetch('/api/reviews');
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : (data.reviews || []));
    } catch (err) {
      console.error('Failed to load reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/user/my-reviews', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyReviews(data);
      }
    } catch (err) {
      console.error('Failed to load my reviews:', err);
    }
  };

  const loadEligibleOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/user/reviewable-orders', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEligibleOrders(data);
      }
    } catch (err) {
      console.error('Failed to load eligible orders:', err);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!selectedOrder || reviewForm.text.length < 10) return;
    
    setSubmitLoading(true);
    setSubmitMsg(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/submit-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...reviewForm,
          orderId: selectedOrder,
          token
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      
      setSubmitMsg({ type: 'ok', text: data.message });
      setReviewForm({ stars: 5, text: '' });
      setSelectedOrder('');
      setShowWriteReview(false);
      loadMyReviews();
      loadEligibleOrders();
      loadPublicReviews();
    } catch (err) {
      setSubmitMsg({ type: 'err', text: err.message });
    } finally {
      setSubmitLoading(false);
    }
  };

  // Calculate stats - combine both sources
  const allReviews = [...reviews, ...externalVouches.map(v => ({ stars: 5, ...v }))];
  
  const avgRating = allReviews.length > 0 
    ? (allReviews.reduce((acc, r) => acc + (r.stars || 5), 0) / allReviews.length).toFixed(1)
    : '5.0';
  
  const ratingCounts = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: allReviews.filter(r => (r.stars || 5) === star).length
  }));

  const maxCount = Math.max(...ratingCounts.map(r => r.count), 1);

  const renderStars = (count, size = 'normal') => {
    return (
      <div className={`rv-stars ${size}`}>
        {[1, 2, 3, 4, 5].map(s => (
          <span key={s} className={`rv-star ${s <= count ? 'active' : ''}`}>★</span>
        ))}
      </div>
    );
  };

  const renderInteractiveStars = (count, onChange) => {
    return (
      <div className="rv-stars interactive">
        {[1, 2, 3, 4, 5].map(s => (
          <button
            key={s}
            type="button"
            className={`rv-star ${s <= count ? 'active' : ''}`}
            onClick={() => onChange(s)}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }).replace(/\//g, '. ');
  };

  return (
    <main className="main-content">
      <div className="reviews-page">
        {/* Header */}
        <div className="rv-header">
          <div className="rv-header-left">
            <div className="rv-header-icon">☆</div>
            <div>
              <h1>Reviews • {allReviews.length}</h1>
              <p>What customers say about us</p>
            </div>
          </div>
          <button className="rv-back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>

        {/* Stats Card */}
        <div className="rv-stats-card">
          <div className="rv-stats-left">
            <div className="rv-big-rating">{avgRating}</div>
            {renderStars(Math.round(parseFloat(avgRating)), 'large')}
            <p className="rv-based-on">Based on {allReviews.length} reviews</p>
          </div>
          <div className="rv-stats-right">
            {ratingCounts.map(({ star, count }) => (
              <div key={star} className="rv-bar-row">
                <span className="rv-bar-label">{star}★</span>
                <div className="rv-bar-track">
                  <div 
                    className="rv-bar-fill" 
                    style={{ width: `${(count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="rv-bar-count">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Write Review Button - Show for everyone */}
        {!showWriteReview && (
          <button className="rv-write-btn" onClick={() => setShowWriteReview(true)}>
            ✍️ Write a Review — Get 5% Off
          </button>
        )}

        {/* External Vouches Link */}
        {/* External Vouches Link - Always show */}
        <a href="https://myvouch.es/outsourced" target="_blank" rel="noopener noreferrer" className="rv-vouches-link">
          <span>See our vouches on</span>
          <span className="rv-vouches-site">myvouch.es/outsourced</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </a>

        {/* Write Review Form or Login Prompt */}
        {showWriteReview && (
          <div className="rv-write-card">
            <div className="rv-write-header">
              <h3>✍️ Write a Review</h3>
              <button className="rv-close-btn" onClick={() => setShowWriteReview(false)}>✕</button>
            </div>
            
            {!isLoggedIn ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎟️</div>
                <p style={{ color: 'var(--text-muted)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Login to write a review and receive your
                </p>
                <p style={{ color: '#06f4ad', fontSize: '1.2rem', fontWeight: 600, marginBottom: '1.5rem' }}>
                  5% Discount Coupon
                </p>
                <Link to="/login" className="rv-submit-btn" style={{ display: 'inline-block', textDecoration: 'none', padding: '0.75rem 2rem' }}>
                  Sign In to Continue
                </Link>
              </div>
            ) : eligibleOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📦</div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  You need a completed order to leave a review.
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  Place an order and come back after it's delivered!
                </p>
              </div>
            ) : (
              <form onSubmit={submitReview}>
                <div className="rv-field">
                  <label>Select Order</label>
                  <select 
                    value={selectedOrder} 
                    onChange={e => setSelectedOrder(e.target.value)}
                    className="rv-select"
                  >
                    <option value="">Choose an order...</option>
                    {eligibleOrders.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.productName} — {formatDate(o.createdAt)}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="rv-field">
                  <label>Rating</label>
                  {renderInteractiveStars(reviewForm.stars, (s) => setReviewForm({...reviewForm, stars: s}))}
                </div>
                
                <div className="rv-field">
                  <label>Your Review</label>
                  <textarea
                    value={reviewForm.text}
                    onChange={e => setReviewForm({...reviewForm, text: e.target.value})}
                    placeholder="Share your experience... (min 10 characters)"
                    rows="3"
                    className="rv-textarea"
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="rv-submit-btn"
                  disabled={submitLoading || !selectedOrder || reviewForm.text.length < 10}
                >
                  {submitLoading ? 'Submitting...' : 'Submit & Get 5% Coupon'}
                </button>
                
                {submitMsg && (
                  <div className={`rv-msg ${submitMsg.type}`}>{submitMsg.text}</div>
                )}
              </form>
            )}
          </div>
        )}

        {/* Reviews Grid */}
        <div className="rv-grid">
          {/* Site Reviews */}
          {reviews.map(review => (
            <div key={review.id} className="rv-card">
              {renderStars(review.stars || 5)}
              <p className="rv-text">{review.text}</p>
              <div className="rv-card-footer">
                <span className="rv-date">{formatDate(review.createdAt)}</span>
                <span className="rv-badge">Outsourced</span>
              </div>
              
              {/* Admin Reply */}
              {review.adminReply && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: 'rgba(6, 244, 173, 0.05)',
                  borderLeft: '3px solid #06f4ad',
                  borderRadius: '0 8px 8px 0'
                }}>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.85rem', 
                    color: 'var(--text-primary)',
                    lineHeight: 1.4
                  }}>{review.adminReply}</p>
                  <p style={{ 
                    margin: '0.5rem 0 0 0', 
                    fontSize: '0.7rem', 
                    color: 'var(--text-muted)' 
                  }}>
                    Reply from <span style={{ color: '#06f4ad' }}>Outsourced</span> at {formatDate(review.adminReplyAt)}
                  </p>
                </div>
              )}
            </div>
          ))}
          
          {/* External Vouches from MyVouch.es */}
          {externalVouches.map((vouch, idx) => (
            <div key={vouch.id || `vouch-${idx}`} className="rv-card rv-external-card">
              {renderStars(vouch.stars || 5)}
              <p className="rv-text">{vouch.text}</p>
              <div className="rv-card-footer">
                <div className="rv-vouch-info">
                  <span className="rv-vouch-author">{vouch.username || 'Anonymous'}</span>
                  {vouch.date && (
                    <span className="rv-date">{vouch.date.split(' ')[0]}</span>
                  )}
                </div>
                <span className="rv-badge rv-badge-external">MyVouch.es</span>
              </div>
            </div>
          ))}
          
          {reviews.length === 0 && externalVouches.length === 0 && (
            <div className="rv-empty">
              <p>No reviews yet. Be the first!</p>
            </div>
          )}
        </div>

        {/* My Reviews Section */}
        {isLoggedIn && myReviews.length > 0 && (
          <div className="rv-my-section">
            <h2>📝 My Reviews</h2>
            <div className="rv-grid">
              {myReviews.map(review => (
                <div key={review.id} className="rv-card rv-my-card">
                  <div className="rv-status-badge">
                    {review.active ? '✓ Published' : '⏳ Pending'}
                  </div>
                  {renderStars(review.stars || 5)}
                  <p className="rv-text">{review.text}</p>
                  <div className="rv-card-footer">
                    <span className="rv-date">{formatDate(review.createdAt)}</span>
                    <span className="rv-badge">Outsourced</span>
                  </div>
                  
                  {/* Admin Reply */}
                  {review.adminReply && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: 'rgba(6, 244, 173, 0.05)',
                      borderLeft: '3px solid #06f4ad',
                      borderRadius: '0 8px 8px 0'
                    }}>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '0.85rem', 
                        color: 'var(--text-primary)',
                        lineHeight: 1.4
                      }}>{review.adminReply}</p>
                      <p style={{ 
                        margin: '0.5rem 0 0 0', 
                        fontSize: '0.7rem', 
                        color: 'var(--text-muted)' 
                      }}>
                        Reply from <span style={{ color: '#06f4ad' }}>Outsourced</span> at {formatDate(review.adminReplyAt)}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Reviews;
